/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PinListSelect.tsx
 🗓️ Created: 2025-01-14
 🧩 Description:
    Reusable component for selecting a PIN List with search.
    Loads PIN lists from the API based on the current domain.

 🔧 Notes:
    - Searchable select component
    - Domain-scoped data
    - Includes "No PIN Required" option

 🔁 Last Updated: 2025-01-14 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { buildApiUrl, getApiHeaders } from '../../config/api';

interface PinList {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  enabled: boolean;
}

interface PinListSelectProps {
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function PinListSelect({
  value,
  onChange,
  placeholder = 'Select PIN List',
  disabled = false,
  className = ''
}: PinListSelectProps) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pinLists, setPinLists] = useState<PinList[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load PIN lists from API
  useEffect(() => {
    const fetchPinLists = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          buildApiUrl('/api/telephony/pin-lists?limit=100'),
          {
            headers: getApiHeaders()
          }
        );

        if (response.ok) {
          const result = await response.json();
          setPinLists(result.data || []);
        }
      } catch (error) {
        console.error('Error loading PIN lists:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPinLists();
  }, []);

  // Get selected PIN list
  const selectedPinList = value != null ? pinLists.find(p => String(p.id) === String(value)) : null;

  // Filter PIN lists based on search
  const filteredPinLists = pinLists.filter(pinList =>
    pinList.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (pinList.description && pinList.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (pinListId: number | null) => {
    onChange(pinListId);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className={`
          w-full px-3 py-2 text-left border rounded-md
          bg-background text-foreground
          border-input
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
          transition-colors
        `}
      >
        <span className={selectedPinList ? '' : 'text-muted-foreground'}>
          {loading ? 'Loading...' : (selectedPinList ? selectedPinList.name : placeholder)}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search PIN lists..."
              className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {/* No PIN Required Option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`
                w-full px-3 py-2 text-left rounded-md
                hover:bg-accent hover:text-accent-foreground
                flex items-center justify-between
                transition-colors
                ${value === null ? 'bg-accent text-accent-foreground' : ''}
              `}
            >
              <div className="flex-1">
                <div className="font-medium">No PIN Required</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Allow calls without PIN authorization
                </div>
              </div>
              {value === null && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </button>

            {/* PIN Lists */}
            {filteredPinLists.length > 0 ? (
              filteredPinLists.map((pinList) => (
                <button
                  key={pinList.id}
                  type="button"
                  onClick={() => handleSelect(pinList.id)}
                  className={`
                    w-full px-3 py-2 text-left rounded-md
                    hover:bg-accent hover:text-accent-foreground
                    flex items-center justify-between
                    transition-colors
                  ${value != null && String(value) === String(pinList.id) ? 'bg-accent text-accent-foreground' : ''}
                    ${!pinList.enabled ? 'opacity-50' : ''}
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {pinList.name}
                      {!pinList.enabled && (
                        <span className="ml-2 text-xs text-muted-foreground">(Disabled)</span>
                      )}
                    </div>
                    {pinList.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {pinList.description}
                      </div>
                    )}
                  </div>
                  {value != null && String(value) === String(pinList.id) && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {searchQuery ? 'No PIN lists found' : 'No PIN lists available'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

